import React, { FC } from 'react'
import { Flex, Spin, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { BaseTableOverview } from 'utils/types'
import { StatValue } from '../VerticalPage/str/components'
import { formatAbbreviatedCurrency } from '../VerticalPage/str/utils'
import styles from './STRTablePage.module.scss'

interface STRSummaryProps {
  overviewData?: BaseTableOverview | null
  isLoading: boolean
}

interface ParsedSummaryData {
  total: number
  matchedLeads: number
  hostsWithUnreported: number
  taxGap: number
}

const parseSummaryData = (overviewData?: BaseTableOverview | null): ParsedSummaryData => {
  const total = overviewData?.total ?? 0
  const summary = overviewData?.summary as Record<string, unknown> | undefined
  const incomeCounts = summary?.incomeCounts as Record<string, number> | undefined

  return {
    total,
    matchedLeads: (summary?.matchedLeads as number) ?? (summary?.hatchedLeads as number) ?? 0,
    hostsWithUnreported: (summary?.hostsWithUnreportedRevenue as number) ?? 0,
    taxGap: incomeCounts?.untaxed ?? 0,
  }
}

const STRSummary: FC<STRSummaryProps> = ({ overviewData, isLoading }) => {
  if (isLoading) {
    return (
      <Flex align='center' justify='center' className={styles.summaryWrapper} style={{ minHeight: 90 }}>
        <Spin />
      </Flex>
    )
  }

  const data = parseSummaryData(overviewData)

  return (
    <div className={styles.summaryWrapper}>
      <div className={styles.summaryDarkBg} />

      <Flex align='center' className={styles.summaryContent}>
        <Flex align='center' className={styles.summaryDarkSide}>
          <div className={styles.summaryDarkGrid}>
            <StatValue variant='dark' label='Total Number of hosts' value={data.total.toLocaleString()} />
            <StatValue variant='dark' label='Matched leads' value={data.matchedLeads.toLocaleString()} />
            <StatValue
              variant='dark'
              label={
                <Flex align='center' gap={6}>
                  <span>Hosts with unreported revenue</span>
                  <Tooltip
                    title='The total number of hosts or STR owners who are under-reporters.'
                    placement='bottom'
                    color='#fff'
                  >
                    <InfoCircleOutlined style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14 }} />
                  </Tooltip>
                </Flex>
              }
              value={data.hostsWithUnreported.toLocaleString()}
            />
            <StatValue
              variant='dark'
              label='Total Estimated unreported Revenue'
              value={data.taxGap ? formatAbbreviatedCurrency(data.taxGap) : '$0'}
              emphasized
            />
          </div>
        </Flex>
      </Flex>
    </div>
  )
}

export default STRSummary
